<template>
    <div>
        <div class="grid grid-cols-2 gap-4 mb-2">
            <div class="col-span-1">
                <Input
                    :label="type === 'flight' ? 'Flight date' : 'Travel date'"
                    :placeholder="type === 'flight' ? 'Flight date' : 'Travel date'"
                    name="start_date"
                    :id="type === 'flight' ? 'depart-date' : 'travel-date'"
                    :options="{ icon: 'calendar', iconPosition: 'left' }"
                    :errors="tripFormErrors"
                >
                    <template #input>
                        <FlatPickr
                            :config="dateStartConfig"
                            v-model="activeRedemption.start_date"
                            :name="type === 'train' ? 'travel_start_date' : 'flight_start_date'"
                            :id="type === 'flight' ? 'depart-date' : 'travel-date'"
                            class="relative block w-full h-10 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pl-10 text-gray-800 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 text-sm placeholder:text-sm"
                            :placeholder="type === 'flight' ? 'Departure date' : 'Travel date'"
                            @on-change="onStartChange"
                            @on-open="onDepartureDateOpen"
                            :class="{ '!border-red-500': tripFormErrors['start_date'] }"
                            :data-hs-id="type === 'flight' ? 'hs-redemption-flight-date' : 'hs-redemption-travel-date'"
                        />
                    </template>
                </Input>
            </div>
            <div class="col-span-1">
                <Input
                    label="Arrival date" placeholder="Arrival date" id="arrival-date"
                    :options="{ icon: 'calendar', iconPosition: 'left' }"
                    v-if="differentArrivalDate"
                >
                    <template #input>
                        <FlatPickr
                            :config="dateEndConfig"
                            v-model="activeRedemption.end_date"
                            :name="type === 'train' ? 'travel_end_date' : 'flight_end_date'"
                            id="arrival-date"
                            class="relative block w-full h-10 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pl-10 text-gray-800 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 text-sm placeholder:text-sm"
                            placeholder="Arrival date"
                            @on-change="onEndChange"
                            :data-hs-id="type === 'flight' ? 'hs-redemption-arrival-date' : 'hs-redemption-train-arrival-date'"
                        />
                    </template>
                </Input>
            </div>
        </div>
        <label class="text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2 mb-8">
            <input
                class="form-checkbox" type="checkbox" value="1"
                :checked="differentArrivalDate"
                @change="$emit('update:differentArrivalDate', $event.target.checked)"
            />
            <span>Arrival date is different</span>
        </label>

        <TravelSegments
            :travel-number-label="type === 'flight' ? 'Flight number' : 'Train number'"
            @segmentsUpdated="handleSegmentsUpdated"
            :segments="segments"
        />

        <div class="grid grid-cols-2 gap-4 mb-8">
            <div class="col-span-1">
                <Input id="number-of-travelers" name="number_of_travelers" label="Number of travelers">
                    <template #input>
                        <input
                            type="number" step="1" min="0" pattern="[0-9]*"
                            placeholder="Number of travelers"
                            class="w-full h-10 text-sm rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 px-3 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            v-model="activeRedemption.number_of_travelers"
                        />
                    </template>
                </Input>
            </div>
            <div class="col-span-1">
                <div>
                    <label class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2">
                        Class of service
                    </label>
                    <UiSingleSelect
                        :options="classOptions"
                        v-model="classOfService"
                        labelKey="label" valueKey="value"
                        :label="classOfService?.label ? classOfService.label : 'Class of service'"
                        buttonClass="w-full font-medium whitespace-nowrap rounded shadow border inline-flex items-center gap-x-1.5 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 hover:border-slate-800 focus:!ring-0 focus:!ring-offset-0 focus:!outline-none focus:!border-slate-800 focus-visible:!ring-0 focus-visible:!outline-none disabled:opacity-50 disabled:text-slate-800 dark:text-slate-100 disabled:bg-white dark:bg-slate-800 disabled:border-slate-300 dark:border-slate-600 justify-between font-normal"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import TravelSegments from '@/components/trips/travel-segments.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import UiSingleSelect from '@/components/ui/inputs/single-select/ui-single-select.vue';

import { computed } from 'vue';

const props = defineProps({
    type: { type: String, required: true },
    dateStartConfig: { type: Object, required: true },
    dateEndConfig: { type: Object, required: true },
    differentArrivalDate: { type: Boolean, required: true },
    onStartChange: { type: Function, required: true },
    onEndChange: { type: Function, required: true },
    onDepartureDateOpen: { type: Function, required: true },
    segments: { type: Array, required: true },
    handleSegmentsUpdated: { type: Function, required: true },
});

defineEmits(['update:differentArrivalDate']);

const { activeRedemption, classOptions, tripFormErrors } = storeToRefs(useTripsStore());

const classOfService = computed({
    get: () => {
        if (typeof activeRedemption.value.class_of_service === 'string') {
            return classOptions.value.find((o) => o.value === activeRedemption.value.class_of_service) || null;
        }
        return activeRedemption.value.class_of_service;
    },
    set: (value) => { activeRedemption.value.class_of_service = value?.value || null; },
});
</script>
